<template>
    <div class="space-y-1">
        <form-label>value columns</form-label>

        <div v-if="order.length === 0" class="text-xs text-slate-500 italic px-1 py-0.5">
            No value columns — series rows show only the label/unit.
        </div>

        <div v-for="(key, idx) in order" :key="key"
            class="flex items-center gap-1 px-1 py-0.5 rounded bg-slate-700/40 cursor-move"
            draggable="true"
            v-on:dragstart="onDragStart(idx)"
            v-on:dragover.prevent
            v-on:drop="onDrop(idx)">
            <span class="text-slate-400 text-xs select-none">⠿</span>
            <span class="flex-1 text-xs text-slate-200">{{ labelFor(key) }}</span>
            <button type="button" class="text-red-400 hover:text-red-300 text-xs px-1"
                title="Remove" v-on:click="remove(idx)">✕</button>
        </div>

        <div class="flex flex-wrap gap-1 pt-1" v-if="available.length">
            <button type="button" v-for="key in available" :key="key"
                class="text-xs px-1.5 py-0.5 rounded border border-slate-500 text-slate-300 hover:bg-slate-600"
                v-on:click="add(key)">+ {{ labelFor(key) }}</button>
        </div>
    </div>
</template>

<script>
// Every value this control lets the user pick from — must stay in sync with
// legend.js's values_order property comment and the firmware's
// hasp_series_stats_t fields (current/min/max/avg).
const ALL_KEYS = ['current', 'min', 'max', 'avg'];
const KEY_LABELS = { current: 'Current', min: 'Min', max: 'Max', avg: 'Avg' };

export default {
    name: 'LegendValuesOrder',

    props: {
        node: { type: Object, required: true },
    },

    data() {
        return { refreshTick: 0 };
    },

    // node is a plain (non-reactive) Konva instance — same reasoning as
    // PartPropertiesPanel.vue's own mounted()/beforeUnmount() pair. Subscribe
    // to this exact style property so external changes (undo/redo) also
    // refresh this control.
    mounted() {
        this._handler = () => { this.refreshTick++; };
        this.node.onStyle('main', 'values_order', this._handler);
    },

    beforeUnmount() {
        this.node.offStyle('main', 'values_order', this._handler);
    },

    computed: {
        order() {
            this.refreshTick; // register as a render dependency, see mounted()
            return this.node.styleProp('main', 'values_order', 'default') ?? [];
        },
        available() {
            return ALL_KEYS.filter(key => !this.order.includes(key));
        },
    },

    methods: {
        labelFor(key) {
            return KEY_LABELS[key] ?? key;
        },

        write(next) {
            this.node.setStyle('main', 'values_order', next);
        },

        add(key) {
            this.write([...this.order, key]);
        },

        remove(idx) {
            const next = [...this.order];
            next.splice(idx, 1);
            this.write(next);
        },

        onDragStart(idx) {
            this._dragIndex = idx;
        },

        onDrop(idx) {
            if (this._dragIndex == null || this._dragIndex === idx) return;
            const next = [...this.order];
            const [moved] = next.splice(this._dragIndex, 1);
            next.splice(idx, 0, moved);
            this._dragIndex = null;
            this.write(next);
        },
    },
};
</script>
